import { useEffect, useState } from "react"
import posthog from "posthog-js"

export interface SessionUser {
  id: string
  email: string
  name: string
  role: string
  organization_id: string
  tier?: string
  billing_status?: string
  billing_interval?: "monthly" | "annual"
  trial_ends_at?: string | null
  organization_name?: string
  organization_slug?: string
  onboarding_complete?: boolean
  entitlements?: Entitlements
}

export interface Entitlements {
  events_per_month: number
  retention_days: number
  audit_retention_days: number
  seats: number
  features: {
    sso: boolean
    multi_org: boolean
    portal_links: boolean
    observability_export: boolean
    long_term_sinks: boolean
    scim: boolean
    ip_allowlist: boolean
    transformations: boolean
    queue_sinks: boolean
    tunnel: boolean
    alerting: boolean
  }
}

export type SessionState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: SessionUser }
  | { status: "unauthenticated"; user: null }

// useRetentionCap reads the org's plan retention limit from the session.
// unlimited covers the Torrent tier and self-host (where the cap is absent).
export function useRetentionCap(): { maxDays: number; unlimited: boolean } {
  const session = useSession()
  const days =
    session.status === "authenticated" ? session.user.entitlements?.retention_days : undefined
  if (days === undefined || days < 0) return { maxDays: 0, unlimited: true }
  return { maxDays: days, unlimited: false }
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: "loading", user: null })

  useEffect(() => {
    let cancelled = false
    fetch("/api/v1/saas/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return
        const body = (await res.json()) as { authenticated: boolean } & Partial<SessionUser>
        if (body.authenticated && body.id) {
          const user = body as SessionUser
          posthog.identify(user.id, {
            email: user.email,
            name: user.name,
            role: user.role,
            organization_id: user.organization_id,
          })
          setState({ status: "authenticated", user })
          return
        }
        posthog.reset()
        setState({ status: "unauthenticated", user: null })
      })
      .catch(() => {
        if (cancelled) return
        posthog.reset()
        setState({ status: "unauthenticated", user: null })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}
